import React, { useState } from "react";
import {
  Option,
  DefaultsPageConfiguration,
  BaselineDefaultsExportTermsFormBedroomType,
  BaselineDefaultsExportTermsForm,
  BaselineDefaultsExportTermsFormPropertyType,
  BaselineDefaultsExportTermsFormDefaults
} from "api";
import createDataTable, {
  IPagedTableState,
  ColumnDefinition,
  DataTableProps
} from "components/controls/createTable";

import Dropdown from "components/controls/Dropdown";
import listUtils from "utils/listUtils";
import Checkbox from "components/controls/Checkbox";
import ValidationWrapper from "components/controls/ValidationWrapper";

export interface DivisionDefaultsTableProps {
  defaults?: BaselineDefaultsExportTermsForm[] | undefined;
  bedrooms: BaselineDefaultsExportTermsFormBedroomType[];
  properties: BaselineDefaultsExportTermsFormPropertyType[];
  onChange: (defaults: BaselineDefaultsExportTermsForm[]) => void;
  hidePropertyColumn?: boolean | undefined;
  hideBedroomTypeColumn?: boolean | undefined;
  hideRemoveRowColumn?: boolean | undefined;
  paging?: boolean | undefined;
  onAddRowAction?: (() => void) | undefined;
  includeRemoveRowColumn?: "show" | "buffer" | undefined;
  pageConfiguration: DefaultsPageConfiguration | undefined;
}

interface TableState extends IPagedTableState {
  divisionFilter?: Option;
  sortField?: any;
  sortOrder?: any;
  sortState?: "unsorted" | "asc" | "desc" | undefined;
}

const DefaultsExportTermsFormTable: React.FC<DivisionDefaultsTableProps> = (
  props
) => {
  const [table, setTable] = useState<TableState>({
    offset: 0,
    limit: 10,
    sortOrder: 1
  });

  const onRowChanged = (
    oldRow: BaselineDefaultsExportTermsFormDefaults,
    newRow: BaselineDefaultsExportTermsFormDefaults
  ) => {
    if (!props.defaults) {
      return;
    }

    props.onChange(
      listUtils.copyListWithNewItem(props.defaults, oldRow, newRow)
    );
  };

  const onRemoveRow = (row: BaselineDefaultsExportTermsFormDefaults) => {
    if (!props.defaults) {
      return;
    }

    const newDefaults = listUtils.copyListWithItemRemoved(props.defaults, row);
    props.onChange(newDefaults);
  };

  const onDefaultsPropertyChange = (
    defaultsRow: BaselineDefaultsExportTermsFormDefaults
  ) => {
    return (
      selectedProperty: BaselineDefaultsExportTermsFormPropertyType | undefined
    ) => {
      const newDefaultsRow: BaselineDefaultsExportTermsFormDefaults = {
        ...defaultsRow,
        selectedPropertyType: selectedProperty
      };

      onRowChanged(defaultsRow, newDefaultsRow);
    };
  };

  const onDefaultsBedroomChange = (
    defaultsRow: BaselineDefaultsExportTermsForm
  ) => {
    return (
      selectedBedroom: BaselineDefaultsExportTermsFormBedroomType | undefined
    ) => {
      const newDefaultsRow: BaselineDefaultsExportTermsFormDefaults = {
        ...defaultsRow,
        selectedBedroomType: selectedBedroom
      };

      onRowChanged(defaultsRow, newDefaultsRow);
    };
  };

  const getPropertyDefaultsBody = function (rowData: any, column: any) {
    const defaults = rowData as BaselineDefaultsExportTermsFormDefaults;

    let dropDown = (
      <Dropdown
        onChange={onDefaultsPropertyChange(defaults)}
        selectedValue={defaults.selectedPropertyType}
        options={props.properties}
        style={{ minWidth: "100%", maxWidth: "100%" }}
        validationMessage={
          defaults.fieldValidationErrors &&
          defaults.fieldValidationErrors["SelectedPropertyType"]
        }
      />
    );

    let label = (
      <div>
        {defaults.selectedPropertyType &&
          defaults.selectedPropertyType.displayValue}
      </div>
    );

    if (props.pageConfiguration && props.pageConfiguration.isEditEnabled) {
      return dropDown;
    } else {
      return label;
    }
  };

  const getBedroomDefaultsBody = function (rowData: any, column: any) {
    const defaults = rowData as BaselineDefaultsExportTermsFormDefaults;

    let dropDown = (
      <Dropdown
        onChange={onDefaultsBedroomChange(defaults)}
        selectedValue={defaults.selectedBedroomType}
        options={props.bedrooms}
        style={{ minWidth: "100%", maxWidth: "100%" }}
        validationMessage={
          defaults.fieldValidationErrors &&
          defaults.fieldValidationErrors["SelectedBedroomType"]
        }
      />
    );

    let label = (
      <div>
        {defaults.selectedBedroomType &&
          defaults.selectedBedroomType.displayValue}
      </div>
    );

    if (props.pageConfiguration && props.pageConfiguration.isEditEnabled) {
      return dropDown;
    } else {
      return label;
    }
  };

  const getCheckboxColumnTemplate = function (
    fieldName: keyof BaselineDefaultsExportTermsFormDefaults
  ) {
    return (row: BaselineDefaultsExportTermsFormDefaults) => {
      return (
        <div className={"termValidation"}>
          <ValidationWrapper
            showErrorState={
              row.fieldValidationErrors && row.fieldValidationErrors["OneMonth"]
                ? true
                : false
            }
          >
            <Checkbox
              checked={row[fieldName] === true}
              disabled={
                !(
                  props.pageConfiguration &&
                  props.pageConfiguration.isEditEnabled
                )
              }
              onChange={(checked: any) => {
                const newDefaultsRow = {
                  ...row
                };
                const value: any = checked === true;
                newDefaultsRow[fieldName] = value;
                onRowChanged(row, newDefaultsRow);
              }}
              validationMessage={
                row.fieldValidationErrors &&
                row.fieldValidationErrors["OneMonth"]
              }
            />
          </ValidationWrapper>
        </div>
      );
    };
  };

  const getColumns = function (): any[] {
    const columns = [] as ColumnDefinition<
      BaselineDefaultsExportTermsFormDefaults,
      keyof BaselineDefaultsExportTermsFormDefaults
    >[];

    const propertyColumnStyle = { width: "20%" };
    const bedroomTypeColumnStyle = { width: "15%" };

    if (!props.hidePropertyColumn) {
      columns.push({
        header: "Property",
        style: propertyColumnStyle,
        body: getPropertyDefaultsBody
      });
    } else {
      columns.push({ header: "", style: propertyColumnStyle });
    }

    if (!props.hideBedroomTypeColumn) {
      columns.push({
        header: "Bedroom",
        style: bedroomTypeColumnStyle,
        body: getBedroomDefaultsBody
      });
    } else {
      columns.push({ header: "", style: bedroomTypeColumnStyle });
    }

    const monthStyle = { width: "5%" };
    return columns.concat([
      {
        field: "oneMonth",
        header: "1mo",
        body: getCheckboxColumnTemplate("oneMonth"),
        style: monthStyle
      },
      {
        field: "twoMonths",
        header: "2mo",
        body: getCheckboxColumnTemplate("twoMonths"),
        style: monthStyle
      },
      {
        field: "threeMonths",
        header: "3mo",
        body: getCheckboxColumnTemplate("threeMonths"),
        style: monthStyle
      },
      {
        field: "fourMonths",
        header: "4mo",
        body: getCheckboxColumnTemplate("fourMonths"),
        style: monthStyle
      },
      {
        field: "fiveMonths",
        header: "5mo",
        body: getCheckboxColumnTemplate("fiveMonths"),
        style: monthStyle
      },
      {
        field: "sixMonths",
        header: "6mo",
        body: getCheckboxColumnTemplate("sixMonths"),
        style: monthStyle
      },
      {
        field: "sevenMonths",
        header: "7mo",
        body: getCheckboxColumnTemplate("sevenMonths"),
        style: monthStyle
      },
      {
        field: "eightMonths",
        header: "8mo",
        body: getCheckboxColumnTemplate("eightMonths"),
        style: monthStyle
      },
      {
        field: "nineMonths",
        header: "9mo",
        body: getCheckboxColumnTemplate("nineMonths"),
        style: monthStyle
      },
      {
        field: "tenMonths",
        header: "10mo",
        body: getCheckboxColumnTemplate("tenMonths"),
        style: monthStyle
      },
      {
        field: "elevenMonths",
        header: "11mo",
        body: getCheckboxColumnTemplate("elevenMonths"),
        style: monthStyle
      },
      {
        field: "twelveMonths",
        header: "12mo",
        body: getCheckboxColumnTemplate("twelveMonths"),
        style: monthStyle
      },
      {
        field: "thirteenMonths",
        header: "13mo",
        body: getCheckboxColumnTemplate("thirteenMonths"),
        style: monthStyle
      },
      {
        field: "fourteenMonths",
        header: "14mo",
        body: getCheckboxColumnTemplate("fourteenMonths"),
        style: monthStyle
      },
      {
        field: "fifteenMonths",
        header: "15mo",
        body: getCheckboxColumnTemplate("fifteenMonths"),
        style: monthStyle
      }
    ]);
  };

  const tableProps: DataTableProps<
    BaselineDefaultsExportTermsForm,
    TableState
  > = {
    value: props.defaults || undefined,
    editMode: "cell",
    state: table,
    updateState: setTable,
    paging: props.paging,
    // TODO: This functionality only works when added at this leve. I was unable to get it to work when I moved it up to the createDataTable function
    sortField: table.sortField,
    sortOrder: table.sortOrder,
    onSort: (e: any) => {
      //clear sort column
      if (
        table.sortOrder === -1 &&
        e.sortOrder === 1 &&
        table.sortField === e.sortField
      ) {
        setTable({
          ...table,
          sortField: undefined,
          sortOrder: undefined
        });
      } else {
        setTable({
          ...table,
          sortField: e.sortField,
          sortOrder: e.sortOrder
        });
      }
    },
    onAddRowAction: props.onAddRowAction,
    onRemoveRowAction:
      props.includeRemoveRowColumn === "show" ? onRemoveRow : undefined,
    hasRemoveRowBuffer: props.includeRemoveRowColumn === "buffer",
    emptyMessage: "No defaults have been added."
  };
  const dataTable = createDataTable<
    BaselineDefaultsExportTermsForm,
    TableState
  >(getColumns(), tableProps);

  return <div>{dataTable}</div>;
};

export default DefaultsExportTermsFormTable;
